import React, { FC, ReactElement } from 'react';
import {
  getLatestValidationEvidenceByEffectiveDate,
  SupplierValidationRecord,
  transformSupplierValidationVerificationRecordEvidence,
  transformVerificationsAndAutoMatch,
} from '@mortee/domain/validationSystem';
import validationSystemServices from '@mortee/services/validationSystemServices';
import useLoadable from '@app/hooks/loadable/useLoadable';
import { HttpStatus, isRequestError } from '@app/libs/request';
import { LoadableCreator } from '@app/utils/Loadable';
import LoadableTransitionLoader from '@app/components/LoadableTransitionLoader';
import NewVerificationTab from './NewVerificationTab';

interface Props {
  className?: string;
  record: SupplierValidationRecord;
  onIsFormUnsavedChanged?: (isUnsaved: boolean) => void;
  onSaved?: (updatedRecord: SupplierValidationRecord | undefined) => void;
}

const NewVerificationTabController: FC<Props> = ({ record, className, onIsFormUnsavedChanged, onSaved }) => {
  const [evidenceLoadable] = useLoadable(async () => {
    try {
      const serverResponse = await validationSystemServices.getVerificationRecordEvidence(record.staticId || '');
      const latestEvidence = getLatestValidationEvidenceByEffectiveDate(serverResponse);
      return latestEvidence ? transformSupplierValidationVerificationRecordEvidence(latestEvidence) : null;
    } catch (e) {
      if (isRequestError(e) && e.code === HttpStatus.notFound) {
        return null;
      }
      throw e;
    }
  }, [record.staticId]);

  const [verificationsAndAutoMatchLoadable] = useLoadable(async () => {
    const serverResponse = await validationSystemServices.getVerificationsAndAutoMatchByStaticId(record.staticId || '');
    return transformVerificationsAndAutoMatch(serverResponse);
  }, [record.staticId]);

  return (
    <LoadableTransitionLoader loadable={LoadableCreator.combine(evidenceLoadable, verificationsAndAutoMatchLoadable)}>
      {([evidence, verificationsAndAutoMatch]): ReactElement => (
        <NewVerificationTab
          record={record}
          className={className}
          evidence={evidence || undefined}
          verificationsAndAutoMatch={verificationsAndAutoMatch}
          onIsFormUnsavedChanged={onIsFormUnsavedChanged}
          onSaved={onSaved}
        />
      )}
    </LoadableTransitionLoader>
  );
};

export default NewVerificationTabController;
